/* eslint-disable react/jsx-props-no-spreading */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { format, isValid } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { createInvoiceApi } from 'api/invoiceGroup';
import perfectRxLogo from 'assets/img/perfectRxLogo.png';

import {
  DEFAULT_CUSTOMER_MESSAGE,
  EMPTY_LINE,
  INVOICE_ACCENT_COLOR,
  currency,
  isEmptyLine,
  isLineValid,
  lineAmount,
  toApiDate,
} from '../utils/invoiceEditHelpers';
import InvoiceLineItemsTable from './InvoiceLineItemsTable';

const InvoiceCreate = () => {
  const { invoiceGroupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Mirrors InvoiceEdit: only reachable by clicking through from the invoice list, which also
  // hands us the group's billTo snapshot so we don't need a separate fetch for the header.
  const cameFromInvoiceList = Boolean(location.state?.fromInvoiceList);
  const billTo = location.state?.billTo;
  const invoiceGroupName = location.state?.invoiceGroupName;
  // billTo and the group name reach us only through router state, which a refresh wipes. The
  // invoice group list is the one place that can supply them again (there's no query for a
  // single group), so a reload has to land there rather than on the invoice list, which would
  // have lost the same state and bounced onward anyway.
  const hasGroupContext = cameFromInvoiceList && Boolean(billTo) && Boolean(invoiceGroupName);

  const [lines, setLines] = useState([{ id: 0, ...EMPTY_LINE }]);
  const [customerMessage, setCustomerMessage] = useState(DEFAULT_CUSTOMER_MESSAGE);
  const [billingPeriodStart, setBillingPeriodStart] = useState(null);
  const [billingPeriodEnd, setBillingPeriodEnd] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [removeIdx, setRemoveIdx] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Line items need a stable React key that survives add/remove, independent of the
  // API's lineNumber (which newly-added rows don't have).
  const nextLineIdRef = useRef(1);

  useEffect(() => {
    document.title = 'New Invoice - SmartOS';
  }, []);

  const handleLineChange = (idx, field, value) =>
    setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, [field]: value } : line)));

  const handleAddRow = () => {
    setLines((prev) => [...prev, { id: nextLineIdRef.current, ...EMPTY_LINE }]);
    nextLineIdRef.current += 1;
  };

  const handleConfirmRemoveRow = () => {
    setLines((prev) => prev.filter((_, i) => i !== removeIdx));
    setRemoveIdx(null);
  };

  const handleRemoveClick = (idx) => {
    if (isEmptyLine(lines[idx])) {
      setLines((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    setRemoveIdx(idx);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const invoiceLineItems = lines.map((line, idx) => ({
        lineNumber: idx + 1,
        ndc: line.ndc,
        drugName: line.drugName,
        description: line.description,
        quantity: Number(line.quantity),
        unitCost: Number(line.unitCost),
      }));

      const created = await createInvoiceApi({
        invoiceGroupId,
        billingPeriodStart: toApiDate(billingPeriodStart),
        billingPeriodEnd: toApiDate(billingPeriodEnd),
        invoiceDate: toApiDate(invoiceDate),
        // Invoices are due on receipt, so the due date is always the invoice date. The server
        // ignores this now; it's still sent so the portal works against a server that requires it.
        dueDate: toApiDate(invoiceDate),
        invoiceLineItems,
        customerMessage,
      });

      setSaveDialogOpen(false);
      enqueueSnackbar(`Invoice #${created.invoiceId} created`, { variant: 'success' });
      // Hand the new invoice's id back so the list can pop its PDF open on arrival, and keep
      // passing billTo along — the list needs it for a subsequent New Invoice.
      navigate(`/adminCenter/invoiceGroups/${invoiceGroupId}`, {
        state: { invoiceGroupName, billTo, openInvoiceId: created.invoiceId },
      });
    } catch (error) {
      // The API rejects a second invoice for the same group + billing period; surface that
      // distinctly so the user knows to change the period rather than just retrying.
      const isDuplicatePeriod = error?.response?.errors?.some(
        (gqlError) => gqlError?.extensions?.code === 'INVOICE_PERIOD_ALREADY_EXISTS'
      );

      enqueueSnackbar(
        isDuplicatePeriod
          ? 'An invoice already exists for this billing period'
          : 'Failed to create invoice',
        { variant: 'error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!hasGroupContext) {
    return <Navigate to='/adminCenter/invoiceGroups' replace />;
  }

  const subtotal = lines.reduce((sum, line) => sum + lineAmount(line), 0);
  const tax = 0;
  const total = subtotal + tax;

  const isPeriodInvalid = Boolean(
    billingPeriodStart && billingPeriodEnd && billingPeriodEnd < billingPeriodStart
  );
  const areDatesComplete = [billingPeriodStart, billingPeriodEnd, invoiceDate].every(
    (date) => date && isValid(date)
  );
  const isFormValid =
    areDatesComplete && !isPeriodInvalid && lines.length > 0 && lines.every(isLineValid);

  const datePickerProps = { inputFormat: 'MM/dd/yyyy' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: 3 }}>
      <Grid
        container
        alignItems='center'
        justifyContent='space-between'
        sx={{ py: 2, flexShrink: 0 }}
      >
        <Grid item container alignItems='center' gap={1} sx={{ width: 'auto' }}>
          <IconButton
            component={Link}
            to={`/adminCenter/invoiceGroups/${invoiceGroupId}`}
            state={{ invoiceGroupName }}
            size='small'
          >
            <ArrowBackIcon fontSize='small' />
          </IconButton>
          <Typography component='h1' variant='h5'>
            New Invoice
          </Typography>
          {invoiceGroupName && (
            <Typography variant='h6' color='text.secondary' sx={{ fontWeight: 400 }}>
              &middot; {invoiceGroupName}
            </Typography>
          )}
        </Grid>
        <Grid item>
          <Button
            variant='contained'
            disabled={!isFormValid}
            onClick={() => setSaveDialogOpen(true)}
          >
            Create
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ flex: 1, overflow: 'auto', pb: 3 }}>
        <Paper sx={{ p: { xs: 2, sm: 4 } }}>
          {/* Header: sender info + invoice meta */}
          <Grid container justifyContent='space-between' alignItems='flex-start' sx={{ mb: 4 }}>
            <Grid item>
              <img src={perfectRxLogo} alt='PerfectRx' height={32} />
              <Typography variant='body2' sx={{ mt: 1, fontWeight: 600 }}>
                PerfectRx LLC
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                ap@perfectrx.com
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                +1 (844) 597-6278
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                www.perfectrx.com
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                variant='h4'
                sx={{
                  color: INVOICE_ACCENT_COLOR,
                  fontWeight: 700,
                  letterSpacing: 1,
                  mb: 1,
                  textAlign: 'right',
                }}
              >
                INVOICE
              </Typography>
              <Grid container spacing={1} alignItems='center' sx={{ width: 360 }}>
                <Grid item xs={5}>
                  <Typography variant='body2' color='text.secondary'>
                    Invoice No.
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    Assigned on create
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant='body2' color='text.secondary'>
                    Terms
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    Due on receipt
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant='body2' color='text.secondary'>
                    Billing Period Start
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <DesktopDatePicker
                    {...datePickerProps}
                    value={billingPeriodStart}
                    maxDate={billingPeriodEnd ?? undefined}
                    onChange={setBillingPeriodStart}
                    renderInput={(params) => (
                      <TextField {...params} size='small' required fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Typography variant='body2' color='text.secondary'>
                    Billing Period End
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <DesktopDatePicker
                    {...datePickerProps}
                    value={billingPeriodEnd}
                    minDate={billingPeriodStart ?? undefined}
                    onChange={setBillingPeriodEnd}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size='small'
                        required
                        fullWidth
                        error={params.error || isPeriodInvalid}
                        helperText={isPeriodInvalid ? 'Must be on or after start' : ''}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Typography variant='body2' color='text.secondary'>
                    Invoice Date
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <DesktopDatePicker
                    {...datePickerProps}
                    value={invoiceDate}
                    onChange={setInvoiceDate}
                    renderInput={(params) => (
                      <TextField {...params} size='small' required fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Typography variant='body2' color='text.secondary'>
                    Due Date
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  {/* Due on receipt: always the invoice date, so it's shown rather than entered. */}
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {invoiceDate && isValid(invoiceDate)
                      ? format(invoiceDate, 'MM/dd/yyyy')
                      : 'Same as invoice date'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Bill To */}
          <Box sx={{ maxWidth: 320, mb: 4 }}>
            <Box sx={{ bgcolor: INVOICE_ACCENT_COLOR, color: 'common.white', px: 1.5, py: 0.5 }}>
              <Typography variant='caption' sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                BILL TO
              </Typography>
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, p: 1.5 }}>
              {billTo ? (
                <>
                  <Typography variant='body2'>
                    {billTo.firstName} {billTo.lastName}
                  </Typography>
                  <Typography variant='body2'>{billTo.streetAddress}</Typography>
                  {billTo.streetAddress2 && (
                    <Typography variant='body2'>{billTo.streetAddress2}</Typography>
                  )}
                  <Typography variant='body2'>
                    {billTo.city}, {billTo.state}, {billTo.zip}
                  </Typography>
                  <Typography variant='body2'>{billTo.email}</Typography>
                  <Typography variant='body2'>{billTo.phone}</Typography>
                </>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Billing details are taken from the invoice group on create.
                </Typography>
              )}
            </Box>
          </Box>

          {/* Line items */}
          <InvoiceLineItemsTable
            lines={lines}
            onLineChange={handleLineChange}
            onRemoveClick={handleRemoveClick}
            onAddRow={handleAddRow}
          />

          {/* Totals */}
          <Grid container justifyContent='flex-end' sx={{ mb: 4 }}>
            <Grid item sx={{ minWidth: 260 }}>
              <Grid container justifyContent='space-between' sx={{ py: 0.5 }}>
                <Typography variant='body2' color='text.secondary'>
                  Subtotal
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {currency(subtotal)}
                </Typography>
              </Grid>
              <Grid container justifyContent='space-between' sx={{ py: 0.5 }}>
                <Typography variant='body2' color='text.secondary'>
                  Tax
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {currency(tax)}
                </Typography>
              </Grid>
              <Grid
                container
                justifyContent='space-between'
                sx={{
                  bgcolor: INVOICE_ACCENT_COLOR,
                  color: 'common.white',
                  px: 1.5,
                  py: 1,
                  mt: 1,
                }}
              >
                <Typography variant='body1' sx={{ fontWeight: 700 }}>
                  TOTAL
                </Typography>
                <Typography variant='body1' sx={{ fontWeight: 700 }}>
                  {currency(total)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Note to customer */}
          <Box>
            <Typography variant='overline' sx={{ fontWeight: 700 }}>
              Note to customer
            </Typography>
            <TextField
              variant='standard'
              fullWidth
              multiline
              minRows={3}
              value={customerMessage}
              onChange={(e) => setCustomerMessage(e.target.value)}
              InputProps={{ sx: { fontStyle: 'italic', fontSize: 'body2.fontSize' } }}
            />
          </Box>
        </Paper>
      </Box>

      <Dialog open={removeIdx !== null} onClose={() => setRemoveIdx(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Remove line item?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            This will remove the line item from the invoice and recalculate the totals.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' color='error' onClick={handleConfirmRemoveRow}>
            Remove
          </Button>
          <Button variant='outlined' onClick={() => setRemoveIdx(null)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Create this invoice?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            {`This will create a new invoice for ${
              billingPeriodStart && isValid(billingPeriodStart)
                ? format(billingPeriodStart, 'MM/dd/yyyy')
                : ''
            } - ${
              billingPeriodEnd && isValid(billingPeriodEnd)
                ? format(billingPeriodEnd, 'MM/dd/yyyy')
                : ''
            } totaling ${currency(total)}.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={handleConfirmSave} disabled={isSaving}>
            Create
          </Button>
          <Button variant='outlined' onClick={() => setSaveDialogOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceCreate;
